import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import AttendanceAdminFilters from "../components/AttendanceAdminFilters";
import AttendanceAdminHeader, {
  CompanySelectOption,
} from "../components/AttendanceAdminHeader";
import AttendanceAdminStats from "../components/AttendanceAdminStats";
import AttendanceAdminTable from "../components/AttendanceAdminTable";
import EditAttendanceModal from "../../shared/modals/EditAttendanceModal";
import ExportModal from "../../shared/modals/ExportModal";
import LogsModal from "../../shared/modals/LogsModal";
import { showBasicAlert } from "../../../../../core/common/useAlert";
import { all_routes } from "../../../../router/all_routes";
import { formatTimeCheckinOut } from "../../../../../helper/attendance_helper";
import {
  getAllComapniesList,
  getAllEmployeesDetailedAttendanceReport,
  getAllEmployeesListDataByCompanyId,
  getAttendanceForExport,
  getAttendenceForAllEmloyees,
  getDepartments,
  getIndividualEmployeeAttendanceReport,
} from "../../../../../services/attendanceService";
import {
  CHECK_IN_THRESHOLD,
  DEFAULT_EMPLOYEE_STATUS,
} from "../constants/attendanceAdmin.constants";
import {
  calcBreakMinutes,
  fetchLeaveInfoMap,
  formatBreakLabel,
  formatLeaveStatus,
} from "../helpers/attendanceAdmin.helper";

import type {
  AttendanceResponse,
  AttendanceStatusFilter,
  CompanyOption,
  DepartmentOption,
  EmployeeStatusFilter,
  TableDataItem,
} from "../types";

const DEFAULT_LIMIT = 25;

const AttendanceAdminPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [employees, setEmployees] = useState<AttendanceResponse | null>(null);
  const [filteredTableData, setFilteredTableData] = useState<TableDataItem[]>([]);

  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [allEmployees, setAllEmployees] = useState<any[]>([]);

  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<DepartmentOption | null>(null);
  const [selectedStatus, setSelectedStatus] =
    useState<AttendanceStatusFilter>("All");
  const [selectedEmpStatus, setSelectedEmpStatus] =
    useState<EmployeeStatusFilter>(DEFAULT_EMPLOYEE_STATUS);
  const [currentDate, setCurrentDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [displayDate, setDisplayDate] = useState<string>(dayjs().format("MMMM D, YYYY"));

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [search, setSearch] = useState("");

  const [showLogs, setShowLogs] = useState(false);
  const [activeRecord, setActiveRecord] = useState<TableDataItem | null>(null);

  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const navigate = useNavigate();

  const [showEditModal, setShowEditModal] = useState(false);
  const [editRecord, setEditRecord] = useState<any>(null);

  const activeCompanyKey = selectedCompany
    ? `${selectedCompany.value}|${selectedCompany.type}`
    : null;

  const companiesForSelect: CompanySelectOption[] = useMemo(
    () =>
      companies.map((c) => ({
        value: `${c.value}|${c.type}`,
        label: c.label,
        companyId: c.value,
        companyType: c.type,
      })),
    [companies],
  );

  const fetchAllEmployees = useCallback(
    async (companyId: string | number, companyType: string) => {
      try {
        const companyInfo = { company_id: companyId, company_type: companyType };
        const response = await getAllEmployeesListDataByCompanyId(companyInfo);
        const employeesData = response?.data?.data?.allUsers || [];
        setAllEmployees(employeesData);
      } catch (error) {
        console.error("Error fetching employees:", error);
        setAllEmployees([]);
      }
    },
    [],
  );

  const buildTableRows = useCallback(
    (data: AttendanceResponse | null, status: AttendanceStatusFilter): TableDataItem[] => {
      if (!data) return [];

      const lateMap = new Map<string, any>();
      (data.late_comers || []).forEach((late: any) => {
        lateMap.set(late.user_id, late);
      });

      const mappedPresent: TableDataItem[] = (data.attendance || []).map(
        (item: any, index: number) => {
          const checkInTime = formatTimeCheckinOut(item.check_in);
          const checkOutTime = formatTimeCheckinOut(item.check_out);

          const lateEntry = lateMap.get(item.user_id);
          let formattedLate = "0m";
          if (lateEntry?.check_in) {
            const lateMinutes = Math.max(
              dayjs(lateEntry.check_in, "HH:mm:ss").diff(
                dayjs(CHECK_IN_THRESHOLD, "HH:mm:ss"),
                "minute",
              ),
              0,
            );
            const h = Math.floor(lateMinutes / 60);
            const m = lateMinutes % 60;
            formattedLate = h > 0 ? `${h}h ${m}m` : `${m}m`;
          }

          return {
            key: `present_${index}`,
            id: item.id,
            user_id: item.user_id,
            essl_id: item.essl_id,
            Employee: item.employee_name,
            Image: item.userProfile,
            Role: "N/A",
            Status: item.status || "Present",
            CheckIn: checkInTime,
            CheckOut: checkOutTime,
            Break: formatBreakLabel(calcBreakMinutes(item.logs)),
            Late: formattedLate,
            Production: item.total_hours ?? 0,
            ProductionHours: item.productive_hours ?? "0 Hrs",
            logs: item.logs,
            edit_logs: item.edit_logs || [],
            raw_check_in: item.check_in,
            raw_check_out: item.check_out,
          };
        },
      );

      const mappedAbsent: TableDataItem[] = (data.absent_employees || []).map(
        (item: any, index: number) => ({
          key: `absent_${index}`,
          id: item.id,
          Employee: item.employee_name,
          Image: item.userProfile || "",
          Role: "N/A",
          Status: "Absent",
          CheckIn: "N/A",
          CheckOut: "N/A",
          Break: "N/A",
          user_id: item.user_id,
          Late: "N/A",
          Production: "0",
          ProductionHours: "0 Hrs",
          logs: item.logs,
          edit_logs: item.edit_logs || [],
        }),
      );

      const combined = [...mappedPresent, ...mappedAbsent];
      const userMap = new Map<string, TableDataItem>();

      combined.forEach((item) => {
        const existing = userMap.get(item.user_id);
        if (!existing) {
          userMap.set(item.user_id, item);
          return;
        }

        if (existing.Status === "Present" && item.Status === "Late") {
          if (!existing.Late || existing.Late === "N/A") existing.Late = item.Late;
        } else if (existing.Status === "Late" && item.Status === "Present") {
          existing.Status = "Present";
          existing.CheckIn = item.CheckIn;
          existing.CheckOut = item.CheckOut;
          existing.Break = item.Break;
          existing.Production = item.Production;
          existing.ProductionHours = item.ProductionHours;
          existing.logs = item.logs;
          if (!existing.Late || existing.Late === "N/A") existing.Late = item.Late;
        } else if (
          existing.Status === "Absent" &&
          (item.Status === "Present" || item.Status === "Late")
        ) {
          existing.Status = item.Status;
          existing.CheckIn = item.CheckIn;
          existing.CheckOut = item.CheckOut;
          existing.Break = item.Break;
          existing.Late = item.Late;
          existing.Production = item.Production;
          existing.ProductionHours = item.ProductionHours;
          existing.logs = item.logs;
        }
      });

      const uniqueData = Array.from(userMap.values());

      if (status === "All") return uniqueData;
      if (status === "Late") {
        return uniqueData.filter(
          (row) => row.Status === "Present" && row.Late !== "0m" && row.Late !== "N/A",
        );
      }
      return uniqueData.filter((row) => row.Status === status);
    },
    [],
  );

  const getAttendence = useCallback(
    async (
      date?: string,
      company?: CompanyOption | null,
      department?: DepartmentOption | null,
      status: AttendanceStatusFilter = selectedStatus,
      empStatus: EmployeeStatusFilter = selectedEmpStatus,
    ) => {
      try {
        setIsLoading(true);
        const selectedDate = date || dayjs().format("YYYY-MM-DD");
        const { data } = await getAttendenceForAllEmloyees(
          selectedDate,
          (company || selectedCompany) as any,
          (department || selectedDepartment) as any,
          empStatus,
        );
        const response: AttendanceResponse | null = data || null;
        setEmployees(response);
        setCurrentDate(selectedDate);
        setDisplayDate(dayjs(selectedDate).format("MMMM D, YYYY"));
        setFilteredTableData(buildTableRows(response, status));
        setPage(1);
      } catch (error) {
        console.error("Error fetching attendance:", error);
        setFilteredTableData([]);
      } finally {
        setIsLoading(false);
      }
    },
    [selectedStatus, selectedEmpStatus, selectedCompany, selectedDepartment, buildTableRows],
  );

  const getCompanies = useCallback(async () => {
    try {
      const data = await getAllComapniesList();
      const list = data?.data?.data?.data || [];
      const mappedCompanies: CompanyOption[] = list.map((comp: any) => ({
        value: comp.id,
        label: `${comp.name} - ${comp.type}`,
        type: comp.type,
      }));

      const deps = await getDepartments();
      const list2 = deps?.data?.data?.data || [];
      const mappedDepartments: DepartmentOption[] = list2.map((d: any) => ({
        value: d.key,
        label: `${d.Department}`,
      }));

      setDepartments(mappedDepartments);
      setCompanies(mappedCompanies);
      if (mappedCompanies.length > 0) {
        const defaultCompany = mappedCompanies[0];
        setSelectedCompany(defaultCompany);
        await fetchAllEmployees(defaultCompany.value, defaultCompany.type);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  }, [fetchAllEmployees]);

  useEffect(() => {
    setIsLoading(true);
    getCompanies();
  }, [getCompanies]);

  useEffect(() => {
    if (selectedCompany) {
      getAttendence(
        currentDate,
        selectedCompany,
        selectedDepartment,
        selectedStatus,
        selectedEmpStatus,
      );
    }
  }, [
    selectedCompany?.value,
    selectedCompany?.type,
    selectedDepartment?.value,
    selectedEmpStatus,
  ]);

  const handleCompanyChange = useCallback(
    async (company: CompanyOption) => {
      setSelectedCompany(company);
      setSelectedDepartment(null);
      await fetchAllEmployees(company.value, company.type);
    },
    [fetchAllEmployees],
  );

  const handleDepartmentChange = useCallback((dep: DepartmentOption | null) => {
    setSelectedDepartment(dep);
  }, []);

  const handleStatusChange = useCallback(
    (status: AttendanceStatusFilter) => {
      setSelectedStatus(status);
      setFilteredTableData(buildTableRows(employees, status));
      setPage(1);
    },
    [employees, buildTableRows],
  );

  const handleDateChange = useCallback(
    (date: string) => {
      setCurrentDate(date);
      setDisplayDate(dayjs(date).format("MMMM D, YYYY"));
      getAttendence(
        date,
        selectedCompany,
        selectedDepartment,
        selectedStatus,
        selectedEmpStatus,
      );
    },
    [
      getAttendence,
      selectedCompany,
      selectedDepartment,
      selectedStatus,
      selectedEmpStatus,
    ],
  );

  const handleEmpStatusChange = useCallback((empStatus: EmployeeStatusFilter) => {
    setSelectedEmpStatus(empStatus);
  }, []);

  const handleResetFilters = useCallback(() => {
    const defaultCompany = companies[0] || null;
    const defaultDate = dayjs().format("YYYY-MM-DD");
    setSelectedStatus("All");
    setSelectedEmpStatus(DEFAULT_EMPLOYEE_STATUS);
    setSelectedDepartment(null);
    setSelectedCompany(defaultCompany);
    setCurrentDate(defaultDate);
    setDisplayDate(dayjs(defaultDate).format("MMMM D, YYYY"));
    setSearch("");
    getAttendence(defaultDate, defaultCompany, null, "All", DEFAULT_EMPLOYEE_STATUS);
  }, [companies, getAttendence]);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const searchedTableData = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return filteredTableData;
    return filteredTableData.filter((row) =>
      (row.Employee || "").toLowerCase().includes(term),
    );
  }, [filteredTableData, search]);

  const handleStatusCardClick = useCallback(
    (status: AttendanceStatusFilter) => {
      setSelectedStatus(status);
      setFilteredTableData(buildTableRows(employees, status));
      setPage(1);
    },
    [employees, buildTableRows],
  );

  const handleViewLogs = useCallback((record: TableDataItem) => {
    setActiveRecord(record);
    setShowLogs(true);
  }, []);

  const handlePageChange = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);

  const handleLimitChange = useCallback((nextLimit: number) => {
    setLimit(nextLimit);
    setPage(1);
  }, []);

  const handleExportReport = useCallback(
    async (exportConfig: any) => {
      setExportLoading(true);
      try {
        let response;
        let exportData: any[] = [];
        let reportTitle = "";
        const isIndividualReport = exportConfig.type === "individual";
        const isCompleteDetail = exportConfig.reportDetail === "complete";
        let employeeReportData: any = null;
        let departmentReportData: any = null;

        if (isIndividualReport) {
          response = await getIndividualEmployeeAttendanceReport(
            exportConfig.startDate.format("YYYY-MM-DD"),
            exportConfig.endDate.format("YYYY-MM-DD"),
            exportConfig.company,
            exportConfig.department,
            exportConfig.employee,
            exportConfig.empStatus || "all",
          );

          if (response && response.data && response.data.employee_report) {
            employeeReportData = response.data.employee_report;
            reportTitle = `Individual Report - ${employeeReportData.employee_info.employee_name}`;

            const departmentMap: { [key: string]: string } = {};
            departments.forEach((dept) => {
              departmentMap[dept.value] = dept.label;
            });

            const individualLeaveMap = await fetchLeaveInfoMap(
              exportConfig.employee?.value,
              exportConfig.startDate,
              exportConfig.endDate,
              exportConfig.company?.value,
              exportConfig.company?.type,
            );

            exportData = employeeReportData.daily_attendance.map((day: any) => {
              const dateKey = dayjs(day.date).format("YYYY-MM-DD");
              const leaveInfo = day.leave_info || individualLeaveMap.get(dateKey);
              return {
                Date: dayjs(day.date).format("DD-MM-YYYY"),
                Day: new Date(day.date).toLocaleDateString("en-US", {
                  weekday: "long",
                }),
                "Check In": day.check_in,
                "Check Out": day.check_out,
                "Total Hours": day.total_hours,
                "Productive Hours": day.productive_hours,
                "Break Hours": day.break_hours,
                Status: formatLeaveStatus(day.status, leaveInfo),
                "Late Minutes":
                  day.late_minutes > 0 ? `${day.late_minutes}m` : "0m",
                "Is Late": day.is_late ? "Yes" : "No",
              };
            });
          } else {
            alert("No individual employee data found");
            setExportLoading(false);
            return;
          }
        } else if (isCompleteDetail) {
          response = await getAllEmployeesDetailedAttendanceReport(
            exportConfig.startDate.format("YYYY-MM-DD"),
            exportConfig.endDate.format("YYYY-MM-DD"),
            exportConfig.company,
            exportConfig.type === "department" ? exportConfig.department : null,
            exportConfig.empStatus || "all",
          );

          if (response && response.data && response.data.department_reports) {
            departmentReportData = response.data;
            reportTitle = isCompleteDetail
              ? `Complete Detailed Report - ${exportConfig.type === "department"
                ? exportConfig.department?.label || "All Departments"
                : "All Employees"
              }`
              : exportConfig.type === "department"
                ? `Department Report - ${exportConfig.department?.label || "All Departments"}`
                : "All Employees Report";

            const departmentMap: { [key: string]: string } = {};
            departments.forEach((dept) => {
              departmentMap[dept.value] = dept.label;
            });

            const esslToUserId = new Map<string, any>();
            allEmployees.forEach((emp: any) => {
              const essl = emp.essl_id ?? emp.esslId ?? emp.employee_id;
              if (essl != null) esslToUserId.set(String(essl), emp.id);
            });

            const employeeUserIds: any[] = [];
            departmentReportData.department_reports.forEach((dept: any) => {
              dept.employees.forEach((employee: any) => {
                const info = employee.employee_info || {};
                const userId =
                  info.user_id ??
                  info.userId ??
                  esslToUserId.get(String(info.employee_id));
                if (userId != null && !employeeUserIds.includes(userId)) {
                  employeeUserIds.push(userId);
                }
              });
            });

            const leaveMaps = await Promise.all(
              employeeUserIds.map((uid) =>
                fetchLeaveInfoMap(
                  uid,
                  exportConfig.startDate,
                  exportConfig.endDate,
                  exportConfig.company?.value,
                  exportConfig.company?.type,
                ).then((map) => [uid, map] as [any, Map<string, any>]),
              ),
            );
            const leaveMapByUser = new Map<any, Map<string, any>>(leaveMaps);

            exportData = [];
            departmentReportData.department_reports.forEach((dept: any) => {
              dept.employees.forEach((employee: any) => {
                const info = employee.employee_info || {};
                const userId =
                  info.user_id ??
                  info.userId ??
                  esslToUserId.get(String(info.employee_id));
                const leaveMap = leaveMapByUser.get(userId);
                employee.daily_attendance.forEach((day: any) => {
                  const dateKey = dayjs(day.date).format("YYYY-MM-DD");
                  const leaveInfo = day.leave_info || leaveMap?.get(dateKey);
                  exportData.push({
                    Department:
                      departmentMap[dept.department_id] || dept.department_name,
                    "Employee Name": info.employee_name,
                    "ESSL ID": info.employee_id,
                    Date: dayjs(day.date).format("DD-MM-YYYY"),
                    Day: new Date(day.date).toLocaleDateString("en-US", {
                      weekday: "long",
                    }),
                    "Check In": day.check_in,
                    "Check Out": day.check_out,
                    "Total Hours": day.total_hours,
                    "Productive Hours": day.productive_hours,
                    "Break Hours": day.break_hours,
                    Status: formatLeaveStatus(day.status, leaveInfo),
                    "Late Minutes":
                      day.late_minutes > 0 ? `${day.late_minutes}m` : "0m",
                    "Is Late": day.is_late ? "Yes" : "No",
                  });
                });
              });
            });
          } else {
            alert("No detailed data found for the selected criteria");
            setExportLoading(false);
            return;
          }
        } else {
          response = await getAttendanceForExport(
            exportConfig.startDate.format("YYYY-MM-DD"),
            exportConfig.endDate.format("YYYY-MM-DD"),
            exportConfig.company,
            exportConfig.type === "department" ? exportConfig.department : null,
            exportConfig.empStatus || "all",
          );

          if (response && response.data && response.data.attendance_summary) {
            const attendanceSummary = response.data.attendance_summary;
            reportTitle =
              exportConfig.type === "department"
                ? `Department Report - ${exportConfig.department?.label || "All Departments"}`
                : "All Employees Report";

            const departmentMap: { [key: string]: string } = {};
            departments.forEach((dept) => {
              departmentMap[dept.value] = dept.label;
            });

            exportData = attendanceSummary.map((employee: any) => ({
              "Employee Name": employee.employee_name,
              "Essl ID": employee.essl_id,
              Department:
                departmentMap[employee.department] || `Dept-${employee.department}`,
              "Total Working Days": employee.total_working_days,
              Present: employee.total_present,
              Absent: employee.total_absent,
              Leaves: employee.total_leaves || 0,
              "Late Arrivals": employee.total_late,
              "Attendance %": employee.attendance_percentage,
            }));
          } else {
            alert("No data found for the selected criteria");
            setExportLoading(false);
            return;
          }
        }

        let filename = `attendance_report_${exportConfig.startDate.format("DD-MM-YYYY")}_to_${exportConfig.endDate.format("DD-MM-YYYY")}`;

        if (exportConfig.type === "department" && exportConfig.department) {
          filename += `_${exportConfig.department.label.replace(/\s+/g, "_")}`;
        } else if (exportConfig.type === "individual" && exportConfig.employee) {
          const employeeName = exportConfig.employee.label.split(" (")[0];
          filename += `_${employeeName.replace(/\s+/g, "_")}`;
        }
        if (isCompleteDetail) filename += `_complete_detail`;

        if (exportConfig.format === "excel") {
          const headers = Object.keys(exportData[0]).join(",");
          const csvContent = exportData
            .map((row: any) =>
              Object.values(row)
                .map((field) => `"${field}"`)
                .join(","),
            )
            .join("\n");
          const fullCsv = headers + "\n" + csvContent;

          const blob = new Blob([fullCsv], { type: "text/csv;charset=utf-8;" });
          const link = document.createElement("a");
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", `${filename}.csv`);
          link.style.visibility = "hidden";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          const printContent = `
        <html>
          <head>
            <title>Attendance ${isIndividualReport ? "Individual" : isCompleteDetail ? "Complete Detailed" : "Summary"} Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
              .summary { margin-bottom: 20px; padding: 15px; background: #f8f9fa; border-radius: 5px; border-left: 4px solid #4e73df; }
              .individual-summary { background: #fff3cd; padding: 15px; border-radius: 5px; margin-bottom: 20px; border-left: 4px solid #ffc107; }
              .complete-summary { background: #d1ecf1; padding: 15px; border-radius: 5px; margin-bottom: 20px; border-left: 4px solid #17a2b8; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: ${isIndividualReport || isCompleteDetail ? "10px" : "12px"}; }
              th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
              th { background-color: #4e73df; color: white; font-weight: bold; }
              .footer { margin-top: 30px; text-align: center; color: #666; font-size: 12px; border-top: 1px solid #ddd; padding-top: 20px; }
              .badge { background: #e9ecef; padding: 4px 8px; border-radius: 4px; font-size: 12px; }
              .present { background-color: #d4edda; }
              .absent { background-color: #f8d7da; }
              .summary-row { background-color: #e9ecef !important; font-weight: bold; }
              .late { background-color: #fff3cd; }
              .department-header { background-color: #6c757d !important; color: white; font-weight: bold; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1 style="color: #2e59d9; margin-bottom: 10px;">
                ${isIndividualReport ? "Individual Attendance Report" : isCompleteDetail ? "Complete Detailed Attendance Report" : "Attendance Summary Report"}
              </h1>
              <h3 style="color: #555; margin-bottom: 5px;">${exportConfig.company?.label}</h3>
              <p style="color: #777;">
                <strong>Period:</strong> ${exportConfig.startDate.format("DD-MM-YYYY")} to ${exportConfig.endDate.format("DD-MM-YYYY")}
              </p>
              ${exportConfig.type === "department" && exportConfig.department ? `<p style="color: #777;"><strong>Department:</strong> ${exportConfig.department.label}</p>` : ""}
              ${exportConfig.type === "individual" && exportConfig.employee ? `<p style="color: #777;"><strong>Employee:</strong> ${exportConfig.employee.label}</p>` : ""}
              ${isCompleteDetail ? `<p style="color: #777;"><strong>Report Type:</strong> Complete Detailed (Daily Attendance)</p>` : ""}
            </div>

            ${isIndividualReport && employeeReportData
              ? (() => {
                const departmentMap: { [key: string]: string } = {};
                departments.forEach((dept) => {
                  departmentMap[dept.value] = dept.label;
                });
                const departmentName =
                  departmentMap[employeeReportData.employee_info.department] ||
                  `Dept-${employeeReportData.employee_info.department}`;
                return `
                      <div class="individual-summary">
                        <h3>Employee Summary</h3>
                        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
                          <div><strong>Employee Name:</strong> ${employeeReportData.employee_info.employee_name}</div>
                          <div><strong>ESSL ID:</strong> ${employeeReportData.employee_info.employee_id}</div>
                          <div><strong>Department:</strong> ${departmentName}</div>
                          <div><strong>Designation:</strong> ${employeeReportData.employee_info.designation}</div>
                          <div><strong>Total Working Days:</strong> ${employeeReportData.report_period.total_working_days}</div>
                          <div><strong>Present Days:</strong> ${employeeReportData.attendance_summary.total_present}</div>
                          <div><strong>Absent Days:</strong> ${employeeReportData.attendance_summary.total_absent}</div>
                          <div><strong>Leave Days:</strong> ${employeeReportData.attendance_summary.total_leaves}</div>
                          <div><strong>Late Days:</strong> ${employeeReportData.attendance_summary.total_late}</div>
                          <div><strong>Attendance %:</strong> ${employeeReportData.attendance_summary.attendance_percentage}%</div>
                        </div>
                      </div>
                    `;
              })()
              : isCompleteDetail && departmentReportData
                ? `
                <div class="complete-summary">
                  <h3>Complete Detailed Report Summary</h3>
                  <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px;">
                    <div><strong>Total Departments:</strong> ${departmentReportData.total_departments}</div>
                    <div><strong>Total Employees:</strong> ${departmentReportData.total_employees}</div>
                    <div><strong>Total Working Days:</strong> ${departmentReportData.report_period.total_working_days}</div>
                    <div><strong>Holidays:</strong> ${departmentReportData.report_period.holidays_count}</div>
                    <div><strong>Reporting Period:</strong> ${departmentReportData.report_period.start_date} to ${departmentReportData.report_period.end_date}</div>
                  </div>
                </div>
              `
                : `
              <div class="summary">
                <strong>Report Summary:</strong><br>
                • Total Employees: ${exportData.length}<br>
                • Reporting Period: ${exportConfig.startDate.format("DD-MM-YYYY")} - ${exportConfig.endDate.format("DD-MM-YYYY")}<br>
                • Generated on: ${dayjs().format("DD-MM-YYYY h:mm A")}
              </div>
            `
            }

            <table>
              <thead>
                <tr>
                  ${isIndividualReport
              ? `
                    <th>Date</th><th>Day</th><th>Check In</th><th>Check Out</th>
                    <th>Total Hours</th><th>Productive Hours</th><th>Break Hours</th>
                    <th>Status</th><th>Late</th>
                  `
              : isCompleteDetail
                ? `
                    <th>Department</th><th>Employee Name</th><th>ESSL ID</th>
                    <th>Date</th><th>Day</th><th>Check In</th><th>Check Out</th>
                    <th>Total Hours</th><th>Productive Hours</th><th>Break Hours</th>
                    <th>Status</th><th>Late</th>
                  `
                : `
                    <th>Employee Name</th><th>Essl ID</th><th>Department</th>
                    <th>Working Days</th><th>Present</th><th>Absent</th>
                    <th>Leaves</th><th>Late Arrivals</th><th>Attendance %</th>
                  `
            }
                </tr>
              </thead>
              <tbody>
                ${isIndividualReport
              ? exportData
                .map((day: any) => {
                  const isSummaryRow = day.Date === "SUMMARY";
                  const isPresent = day.Status === "Present";
                  const isAbsent = day.Status === "Absent";
                  return `
                            <tr class="${isSummaryRow ? "summary-row" : ""} ${isPresent ? "present" : ""} ${isAbsent ? "absent" : ""} ${day["Is Late"] === "Yes" ? "late" : ""}">
                              <td>${day.Date}</td>
                              <td>${day.Day}</td>
                              <td>${day["Check In"]}</td>
                              <td>${day["Check Out"]}</td>
                              <td>${day["Total Hours"]}</td>
                              <td>${day["Productive Hours"]}</td>
                              <td>${day["Break Hours"]}</td>
                              <td>${day.Status}</td>
                              <td>${day["Is Late"] === "Yes" ? `${day["Late Minutes"]}` : "No"}</td>
                            </tr>
                          `;
                })
                .join("")
              : isCompleteDetail
                ? (() => {
                  let currentDept = "";
                  let rows = "";
                  exportData.forEach((record: any) => {
                    if (record.Department !== currentDept) {
                      currentDept = record.Department;
                      rows += `
                                <tr class="department-header">
                                  <td colspan="11"><strong>Department: ${currentDept}</strong></td>
                                </tr>
                              `;
                    }
                    const isPresent = record.Status === "Present";
                    const isAbsent = record.Status === "Absent";
                    rows += `
                              <tr class="${isPresent ? "present" : ""} ${isAbsent ? "absent" : ""} ${record["Is Late"] === "Yes" ? "late" : ""}">
                                <td>${record.Department}</td>
                                <td>${record["Employee Name"]}</td>
                                <td>${record["ESSL ID"]}</td>
                                <td>${record.Date}</td>
                                <td>${record.Day}</td>
                                <td>${record["Check In"]}</td>
                                <td>${record["Check Out"]}</td>
                                <td>${record["Total Hours"]}</td>
                                <td>${record["Productive Hours"]}</td>
                                <td>${record["Break Hours"]}</td>
                                <td>${record.Status}</td>
                                <td>${record["Is Late"] === "Yes" ? `${record["Late Minutes"]}` : "No"}</td>
                              </tr>
                            `;
                  });
                  return rows;
                })()
                : exportData
                  .map(
                    (employee: any) => `
                            <tr>
                              <td><strong>${employee["Employee Name"]}</strong></td>
                              <td><span class="badge">${employee["Essl ID"]}</span></td>
                              <td>${employee["Department"]}</td>
                              <td>${employee["Total Working Days"]}</td>
                              <td style="color: #28a745; font-weight: bold;">${employee["Present"]}</td>
                              <td style="color: #dc3545;">${employee["Absent"]}</td>
                              <td>${employee["Leaves"]}</td>
                              <td style="color: #ffc107;">${employee["Late Arrivals"]}</td>
                              <td style="font-weight: bold; color: ${parseFloat(employee["Attendance %"]) >= 80 ? "#28a745" : parseFloat(employee["Attendance %"]) >= 60 ? "#ffc107" : "#dc3545"}">${employee["Attendance %"]}%</td>
                            </tr>
                          `,
                  )
                  .join("")
            }
              </tbody>
            </table>

            <div class="footer">
              <p>Generated by Attendance Management System</p>
              <p>© ${new Date().getFullYear()} Your Company Name. All rights reserved.</p>
            </div>
          </body>
        </html>
      `;
          const printWindow = window.open("", "_blank");
          if (printWindow) {
            printWindow.document.write(printContent);
            printWindow.document.close();
            printWindow.print();
          }
        }

        setShowExportModal(false);
      } catch (error: any) {
        console.error("Error exporting data:", error);
        showBasicAlert(error?.data?.message?.[0]?.msg);
      } finally {
        setExportLoading(false);
      }
    },
    [departments, allEmployees],
  );

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <AttendanceAdminHeader
            companies={companiesForSelect}
            activeCompanyKey={activeCompanyKey}
            companiesLoading={companies.length === 0}
            exportDisabled={!selectedCompany}
            payrollDisabled={!selectedCompany}
            onCompanyChange={(opt) => {
              const match = companies.find(
                (c) => c.value === opt.companyId && c.type === opt.companyType,
              );
              if (match) handleCompanyChange(match);
            }}
            onExportClick={() => setShowExportModal(true)}
            onPayrollClick={() => navigate(all_routes.payrollReport)}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 16px",
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              marginBottom: 16,
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div className="d-flex align-items-center" style={{ gap: 12 }}>
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: "#eff6ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <i
                  className="ti ti-calendar-event"
                  style={{ color: "#1d71e3", fontSize: 18 }}
                />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: "#64748b",
                    fontWeight: 500,
                    letterSpacing: 0.3,
                    textTransform: "uppercase",
                  }}
                >
                  Showing attendance for
                </div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: "#0f172a",
                    lineHeight: 1.25,
                    marginTop: 2,
                  }}
                >
                  {displayDate}
                </div>
              </div>
            </div>

            <div
              className="d-inline-flex align-items-center"
              style={{
                gap: 6,
                padding: "6px 12px",
                borderRadius: 999,
                background: "#f1f5f9",
                color: "#475569",
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#22c55e",
                  display: "inline-block",
                }}
              />
              <span>
                Updated{" "}
                <strong style={{ color: "#0f172a", fontWeight: 600 }}>
                  {dayjs().format("h:mm A")}
                </strong>
              </span>
            </div>
          </div>

          <AttendanceAdminStats
            data={employees}
            displayDate={displayDate}
            activeStatus={selectedStatus}
            onSelect={handleStatusCardClick}
          />

          <AttendanceAdminFilters
            status={selectedStatus}
            empStatus={selectedEmpStatus}
            departmentValue={selectedDepartment?.value || null}
            date={currentDate}
            departments={departments}
            search={search}
            onSearchChange={handleSearchChange}
            onStatusChange={handleStatusChange}
            onEmpStatusChange={handleEmpStatusChange}
            onDepartmentChange={handleDepartmentChange}
            onDateChange={handleDateChange}
            onReset={handleResetFilters}
          />

          <AttendanceAdminTable
            rows={searchedTableData}
            loading={isLoading}
            page={page}
            limit={limit}
            total={searchedTableData.length}
            companyId={selectedCompany?.value || null}
            companyType={selectedCompany?.type || null}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            onViewLogs={handleViewLogs}
          />
        </div>

        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              ZYVO
            </Link>
          </p>
        </div>
      </div>

      <LogsModal
        open={showLogs}
        onClose={() => setShowLogs(false)}
        employee={activeRecord?.Employee}
        logs={activeRecord?.logs}
      />

      <EditAttendanceModal
        visible={showEditModal}
        onClose={() => setShowEditModal(false)}
        record={editRecord}
        onSuccess={() => {
          setShowEditModal(false);
          getAttendence(
            currentDate,
            selectedCompany,
            selectedDepartment,
            selectedStatus,
            selectedEmpStatus,
          );
        }}
      />

      <ExportModal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        onExport={handleExportReport}
        companies={companies}
        departments={departments}
        employees={allEmployees}
        selectedCompany={selectedCompany}
        exportLoading={exportLoading}
      />
    </>
  );
};

export default AttendanceAdminPage;
